import type { ThreadPlacement } from "../../../api/threads/placementsApi";
import type { ThreadSummary } from "../../../api/threads/threadDecoders";
import { inboxPlace, type InboxPlace } from "./inboxPlacement";
import { waitsOnTheInbox, type InboxSubjects } from "./inboxRuns";

/**
 * The person's Thread list in sections, as T3 Code's sidebar has them (F44; Johannes, 27 Oct:
 * "sorted and being pinnable in the sidebar like in t3 code ... by category"): Pinned, Active,
 * Working, Shared, Snoozed, Settled, in that order. The one owner of which section a Thread is
 * in and of each section's order. Pure.
 *
 * A Thread is in the first that fits: snoozed, settled, pinned, shared (someone else hosts it;
 * F109, "Just because a thread was shared to us does not mean we can't do that through showing
 * it in a category on the left. Just like settled is one."), working, else active (Woke Threads
 * among them). Live, Saved and Closed are statuses, not sections: they show as the row's badge
 * in whichever section it is. A Run the inbox's Waiting for you leads into (`inInbox`) is reached there: it isn't
 * listed again in Active or Shared (run-frame P3), while a pin, a snooze or a settle keeps its own place.
 */
export type ThreadSection = "pinned" | "active" | "working" | "shared" | "snoozed" | "settled";

export const THREAD_SECTIONS: readonly ThreadSection[] = ["pinned", "active", "working", "shared", "snoozed", "settled"];

export interface PlacedThread {
  readonly summary: ThreadSummary;
  readonly placement: ThreadPlacement | undefined;
  readonly place: InboxPlace;
}

export type ThreadSections = Readonly<Record<ThreadSection, readonly PlacedThread[]>>;

const NONE: InboxSubjects = new Map();

function sectionOf(thread: PlacedThread, currentUserId: string | null): ThreadSection {
  if (thread.place === "snoozed" || thread.place === "settled") return thread.place;
  if (thread.placement?.pinnedAt != null) return "pinned";
  if (currentUserId !== null && thread.summary.host !== currentUserId) return "shared";
  // A turn waiting for a free slot on the host's PC starts by itself, as work does (X-2).
  const status = thread.summary.projection.display_status;
  return status === "working" || status === "waiting" ? "working" : "active";
}

const time = (iso: string | null | undefined): number => {
  const parsed = iso == null ? Number.NaN : Date.parse(iso);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** Newest first by `key`; ties by Thread ID, so the order is stable. */
function newestFirst(key: (thread: PlacedThread) => number) {
  return (left: PlacedThread, right: PlacedThread) => key(right) - key(left) || left.summary.threadId.localeCompare(right.summary.threadId);
}

/** When it came back from a snooze: woken by the Thread, or its time passed. */
function wakeTime(thread: PlacedThread, now: number): number {
  const placement = thread.placement;
  if (placement?.state === "woke") return time(placement.wokeAt);
  const until = time(placement?.snoozedUntil);
  return placement?.state === "snoozed" && until <= now ? until : 0;
}

/**
 * T3 Code's orders (its `Sidebar.logic` and `threadSort`), with module C's activity times (Sol
 * S3): Active by the latest of start, return, turn requested, turn finished and wake; Working by
 * the later of start and what a person last wrote; Pinned by drag order, then newest Thread.
 */
const ORDER: Readonly<Record<ThreadSection, (now: number) => (left: PlacedThread, right: PlacedThread) => number>> = {
  pinned: () => (left, right) => {
    // T3 Code's `threadSort`: dragged pins by their place, then the others newest Thread first.
    const leftRank = left.placement?.pinRank ?? Number.POSITIVE_INFINITY;
    const rightRank = right.placement?.pinRank ?? Number.POSITIVE_INFINITY;
    if (leftRank !== rightRank) return leftRank - rightRank;
    return newestFirst((thread) => time(thread.summary.createdAt))(left, right);
  },
  active: (now) =>
    newestFirst((thread) =>
      Math.max(
        time(thread.summary.createdAt),
        time(thread.placement?.returnedAt),
        time(thread.summary.latestTurnRequestedAt),
        time(thread.summary.latestTurnFinishedAt),
        wakeTime(thread, now),
      ),
    ),
  working: () => newestFirst((thread) => Math.max(time(thread.summary.createdAt), time(thread.summary.latestPersonWrittenAt))),
  shared: (now) => ORDER.active(now),
  snoozed: () => (left, right) =>
    time(left.placement?.snoozedUntil) - time(right.placement?.snoozedUntil) || left.summary.threadId.localeCompare(right.summary.threadId),
  settled: () => newestFirst((thread) => time(thread.placement?.settledAt)),
};

/** `currentUserId`: whose list it is, so a Thread someone else hosts is Shared; `null` before the session is known. */
export function threadSections(
  threads: readonly ThreadSummary[],
  placements: ReadonlyMap<string, ThreadPlacement>,
  now: number,
  currentUserId: string | null,
  inInbox: InboxSubjects = NONE,
): ThreadSections {
  const sections: Record<ThreadSection, PlacedThread[]> = { pinned: [], active: [], working: [], shared: [], snoozed: [], settled: [] };
  for (const summary of threads) {
    const placement = placements.get(summary.threadId);
    const thread = { summary, placement, place: inboxPlace(placement, now) };
    const section = sectionOf(thread, currentUserId);
    // One entrance per task (run-frame P3): the inbox's row leads into the review this Run waits on.
    if ((section === "active" || section === "shared") && waitsOnTheInbox(summary, inInbox)) continue;
    sections[section].push(thread);
  }
  for (const section of THREAD_SECTIONS) sections[section].sort(ORDER[section](now));
  return sections;
}
